import type { SensorMaintenanceState } from '../types/sensorMaintenanceLens.types';

const cdnBase = import.meta.env.VITE_AZURE_CDN;
const SENSOR_SVG_URL = `${cdnBase}img/InfraIcons/svg/SENSOR.svg`;

const ICON_SIZE = 200;

export const SENSOR_STATE_COLORS: Record<SensorMaintenanceState, string> = {
	active: '#04628C',
	inactive: '#F46A6A',
	withoutData: '#A6B0BD',
};

const iconCache = new Map<string, string>();
let baseImagePromise: Promise<HTMLImageElement> | null = null;

function loadBaseImage(): Promise<HTMLImageElement> {
	if (!baseImagePromise) {
		baseImagePromise = new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = (error) => {
				baseImagePromise = null;
				reject(error);
			};
			img.src = SENSOR_SVG_URL;
		});
	}
	return baseImagePromise;
}

function drawTintedGlyph(
	ctx: CanvasRenderingContext2D,
	img: HTMLImageElement,
	color: string,
): void {
	const glyphCanvas = document.createElement('canvas');
	glyphCanvas.width = ICON_SIZE;
	glyphCanvas.height = ICON_SIZE;
	const glyphCtx = glyphCanvas.getContext('2d');
	if (!glyphCtx) return;

	const glyphSize = ICON_SIZE * 0.5;
	const offset = (ICON_SIZE - glyphSize) / 2;
	glyphCtx.drawImage(img, offset, offset, glyphSize, glyphSize);
	glyphCtx.globalCompositeOperation = 'source-in';
	glyphCtx.fillStyle = color;
	glyphCtx.fillRect(0, 0, ICON_SIZE, ICON_SIZE);

	ctx.drawImage(glyphCanvas, 0, 0);
}

function buildIcon(img: HTMLImageElement, color: string, selected: boolean): string {
	const canvas = document.createElement('canvas');
	canvas.width = ICON_SIZE;
	canvas.height = ICON_SIZE;
	const ctx = canvas.getContext('2d');
	if (!ctx) return SENSOR_SVG_URL;

	const center = ICON_SIZE / 2;
	const discRadius = center * 0.8;

	ctx.clearRect(0, 0, ICON_SIZE, ICON_SIZE);

	ctx.beginPath();
	ctx.arc(center, center, discRadius, 0, Math.PI * 2);
	ctx.fillStyle = '#FFFFFF';
	ctx.fill();

	drawTintedGlyph(ctx, img, selected ? '#22C55E' : color);

	return canvas.toDataURL('image/png');
}

export async function generateSensorIcon(
	state: SensorMaintenanceState,
	selected = false,
): Promise<string> {
	const cacheKey = `${state}-${selected ? 'selected' : 'default'}`;
	const cached = iconCache.get(cacheKey);
	if (cached) return cached;

	const img = await loadBaseImage();
	const url = buildIcon(img, SENSOR_STATE_COLORS[state], selected);
	iconCache.set(cacheKey, url);
	return url;
}
